import { useSuperQuery } from '@vadiun/react-hooks-legacy';
import useClientBusiness from 'app/client/clientBusiness/services/useClientBusiness';
import useClientPrivates from 'app/client/private/private/services/ClientPrivateService';
import { useQuery } from 'react-query';

/**
 * Hook personalizado para obtener nombres de clientes (empresariales y particulares)
 * y proporcionar una función helper para obtener el nombre de un cliente por ID y tipo
 */
export const useClientNames = (enablePrivateClients: boolean = true) => {
  const clientBusinessRepository = useClientBusiness();
  const ClientPrivateService = useClientPrivates();

  // Cargar clientes empresariales
  const clientesQuery = useSuperQuery(clientBusinessRepository.getAll);

  // Cargar clientes particulares (solo si está habilitado)
  const clientprivatesQuery = useQuery(
    ['private-clients-hook'],
    () => ClientPrivateService.getClientPrivates(),
    {
      enabled: enablePrivateClients
    }
  );

  const clientprivatesAppQuery = useQuery(
    ['private-clients-app-hook'],
    () => ClientPrivateService.getClientPrivatesApp(),
    {
      enabled: enablePrivateClients
    }
  );

  /**
   * Obtiene el nombre de un cliente basado en su ID y tipo
   * @param clientId - ID del cliente
   * @param clientType - Tipo de cliente: 'ParticularClient', 'BusinessClient', o 'AllClient'
   * @returns Nombre del cliente o el ID como string si no se encuentra
   */
  const getClientName = (
    clientId: number,
    clientType?: 'BusinessClient' | 'ParticularClient' | 'ParticularApp' | 'AllClient'
  ): string => {
    if (clientType === 'ParticularClient') {
      const client = (clientprivatesQuery.data ?? []).find((x) => x.id === clientId);
      if (client) {
        return `${client.name} ${client.lastName}`.trim();
      }
    } else if (clientType === 'ParticularApp') {
      const client = (clientprivatesAppQuery.data ?? []).find((x) => x.id === clientId);
      if (client) {
        return `${client.name} ${client.lastName}`.trim();
      }
    } else {
      const client = (clientesQuery.data ?? []).find((x) => x.id === clientId);
      if (client?.fantasyName) {
        return client.fantasyName;
      }
    }
    return clientId.toString();
  };

  /**
   * Obtiene el objeto completo del cliente basado en su ID y tipo
   * @param clientId - ID del cliente
   * @param clientType - Tipo de cliente
   * @returns Objeto del cliente o undefined si no se encuentra
   */
  const getClient = (
    clientId: number,
    clientType?: 'BusinessClient' | 'ParticularClient' | 'ParticularApp' | 'AllClient'
  ) => {
    if (clientType === 'ParticularClient') {
      return (clientprivatesQuery.data ?? []).find((x) => x.id === clientId);
    } else if (clientType === 'ParticularApp') {
      return (clientprivatesAppQuery.data ?? []).find((x) => x.id === clientId);
    } else {
      return (clientesQuery.data ?? []).find((x) => x.id === clientId);
    }
  };

  return {
    clientesQuery,
    clientprivatesQuery,
    clientprivatesAppQuery,
    getClientName,
    getClient,
    isLoading: clientesQuery.isLoading || clientprivatesQuery.isLoading || clientprivatesAppQuery.isLoading
  };
};

